/**
 * lib/departments.ts
 * ------------------------------------------------------------------
 * THE single source of truth for "what department does X belong to".
 *
 * Vocabulary: department names as entered on Teacher.department and
 * AppUser.hodDepartments (e.g. 'Mathematics', 'Sciences'). NOT to be
 * confused with Subject.category, which is a coarser subject-family
 * taxonomy ('sciences', 'core', 'commercial', …) kept for display only.
 *
 * A subject's department resolves by precedence:
 *   1. explicit Subject.department  (admin override — authoritative)
 *   2. majority vote of the teachers who teach it (Teacher.department)
 *   3. UNASSIGNED
 *
 * All comparison is normalised (trim + case-insensitive) so free-text
 * names like ' mathematics' and 'Mathematics' never fork — this is what
 * removes the old "hodDepartments must match teacher records exactly"
 * fragility. Resolve once here; never re-derive department anywhere else.
 * ------------------------------------------------------------------
 */
import type { Subject, Teacher } from '@/types';

export const UNASSIGNED = 'Unassigned';

/** Non-academic buckets that must NEVER appear in academic department pickers
 *  or analysis (Administration is a staff home-department, not a teaching one;
 *  Unassigned is a fallback). Excluded centrally by allDepartments(). */
export const NON_ACADEMIC_DEPARTMENTS = new Set(['administration', 'unassigned']);

/** Normalised comparison key (trim + lowercase). */
export function deptKey(d: string | null | undefined): string {
  return (d ?? '').trim().toLowerCase();
}

/** True iff two department names refer to the same department (and are
 *  both non-empty). */
export function sameDept(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const ka = deptKey(a);
  return ka !== '' && ka === deptKey(b);
}

/** Does this teacher teach the given subject (per assignment maps)? */
function teacherTeaches(t: Teacher, subjectId: string): boolean {
  return (
    Object.keys(t.classAssignments ?? {}).includes(subjectId) ||
    Object.values(t.teachingAssignments ?? {}).some((arr) =>
      arr?.includes(subjectId),
    )
  );
}

/**
 * Canonical department of a SUBJECT.
 * explicit Subject.department → teacher majority vote → UNASSIGNED.
 */
export function departmentOfSubject(
  subject: Subject,
  teachers: Teacher[],
): string {
  const explicit = (subject.department ?? '').trim();
  if (explicit) return explicit;

  const counts: Record<string, { display: string; n: number }> = {};
  for (const t of teachers) {
    const d = (t.department ?? '').trim();
    if (!d) continue;
    if (!teacherTeaches(t, subject.id)) continue;
    const k = deptKey(d);
    if (!counts[k]) counts[k] = { display: d, n: 0 };
    counts[k].n += 1;
  }

  let best = UNASSIGNED;
  let bestN = 0;
  for (const { display, n } of Object.values(counts)) {
    if (n > bestN) {
      best = display;
      bestN = n;
    }
  }
  return best;
}

/** Teacher's home department (normalised display, or UNASSIGNED). */
export function departmentOfTeacher(t: Teacher | null | undefined): string {
  const d = (t?.department ?? '').trim();
  return d || UNASSIGNED;
}

/** Subjects belonging to a department (canonical resolution). */
export function subjectsInDepartment(
  subjects: Subject[],
  teachers: Teacher[],
  dept: string,
): Subject[] {
  return subjects.filter((s) => sameDept(departmentOfSubject(s, teachers), dept));
}

/**
 * Canonical, de-duplicated, sorted department list: the union of every
 * teacher's department and every subject's resolved department, deduped
 * case-insensitively (first-seen casing wins). UNASSIGNED, if present,
 * is appended last.
 */
export function allDepartments(
  subjects: Subject[],
  teachers: Teacher[],
): string[] {
  const seen = new Map<string, string>(); // key -> display
  const add = (d: string | null | undefined) => {
    const t = (d ?? '').trim();
    if (!t) return;
    const k = deptKey(t);
    if (!seen.has(k)) seen.set(k, t);
  };
  teachers.forEach((t) => add(t.department));
  for (const s of subjects) {
    const d = departmentOfSubject(s, teachers);
    if (deptKey(d) !== deptKey(UNASSIGNED)) add(d);
  }
  // Academic surfaces never list the non-academic buckets (Administration,
  // Unassigned) — they're excluded here so every caller is consistent.
  return [...seen.values()]
    .filter((d) => !NON_ACADEMIC_DEPARTMENTS.has(deptKey(d)))
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Is `dept` visible under a scope from curriculumDepartmentsFor?
 *   null  → wildcard (admin / head): everything visible
 *   []    → nothing visible
 *   [...] → visible iff dept matches one (normalised)
 */
export function isDeptVisible(dept: string, scope: string[] | null): boolean {
  if (scope === null) return true;
  return scope.some((d) => sameDept(d, dept));
}
